// PublicLandingPage.jsx
import React, {
  Suspense,
  lazy,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { Link, useHistory } from "react-router-dom";
import {
  ArrowRight,
  ChevronLeft,
  ChevronRight,
  Handshake,
  HandCoins,
  Network,
  Scale,
  Store,
  X,
  HelpCircle,
  Users,
  Globe2,
  Vote as VoteIcon,
  Wheat,
  Workflow,
} from "lucide-react";
import crownLogo from "../../resources/CrownCrackedWithTYK.png";
import FishersOfMen from "../../resources/Fisherman.jpg";
import wheatfieldVideo from "../../resources/wheatfield.mp4";
import { useTranslation } from "../../i18n";
import LearnModal from "./LearnModal";
import ExploreModal from "./ExploreModal";
import Chat from "../Chat";
import {
  trackMetaPixel,
  trackPageView,
  enableMetaPixelDebug,
  getMetaPixelDiagnostics,
} from "../../lib/metaPixel";
import { captureCampaignParams } from "../../lib/marketingAttribution";
// Lazy-loaded heavy components
const GlobeWithPins = lazy(() => import("../GlobeWithPins"));

/* ---------------------------------- */
/* Layout primitives                  */
/* ---------------------------------- */
const Container = ({ className = "", children }) => (
  <div
    className={`mx-auto w-full px-4 sm:px-6 lg:px-8 max-w-[92vw] sm:max-w-5xl lg:max-w-6xl ${className}`}
  >
    {children}
  </div>
);

const Section = ({ id, title, className = "", children }) => (
  <section id={id} className={`relative z-10 py-12 sm:py-16 ${className}`}>
    <Container>
      {title ? (
        <h2 className="text-3xl md:text-4xl font-bold text-white text-center mb-12">
          {title}
        </h2>
      ) : null}
      {children}
    </Container>
  </section>
);

/* ---------------------------------- */
/* Page                               */
/* ---------------------------------- */
export function PublicLandingPage() {
  // Centralized control of the Guide (single funnel)
  const { loginWithRedirect, isAuthenticated, isLoading: authLoading } = useAuth0();
  const history = useHistory();
  const [guideOpen, setGuideOpen] = useState(false);
  const [learnOpen, setLearnOpen] = useState(false);
  const [exploreOpen, setExploreOpen] = useState(false);
  const [joinModalOpen, setJoinModalOpen] = useState(false);
  const isProd = process.env.NODE_ENV === "production";
  const campaignParamsRef = useRef({});

  useEffect(() => {
    if (typeof window !== "undefined") {
      const params = new URLSearchParams(window.location.search);
      if (
        process.env.NODE_ENV !== "production" ||
        params.get("metaPixelDebug") === "true"
      ) {
        enableMetaPixelDebug();
      }
      window.tykMetaPixelDiagnostics = getMetaPixelDiagnostics;
      window.tykPublicLandingEvents = () =>
        Array.isArray(window.__TYK_PUBLIC_LANDING_EVENTS__)
          ? [...window.__TYK_PUBLIC_LANDING_EVENTS__]
          : [];
    }

    const params = captureCampaignParams();
    campaignParamsRef.current = params;
    trackPageView({
      page: "PublicLanding",
      path: window.location.pathname,
      ...params,
    });
  }, []);

  const mergeCampaignParams = useCallback(
    (extra = {}) => ({
      ...campaignParamsRef.current,
      ...extra,
      page: "PublicLanding",
      path: window.location.pathname,
    }),
    []
  );

  const trackCtaClick = useCallback(
    (ctaLabel = "unknown") => {
      const label =
        typeof ctaLabel === "string" && ctaLabel.trim()
          ? ctaLabel.trim()
          : "unknown";

      try {
        trackMetaPixel("PublicLandingCTA", mergeCampaignParams({ cta_label: label }));
      } catch (error) {
        console.warn("Meta Pixel tracking failed for CTA click", error);
      }
    },
    [mergeCampaignParams]
  );

 useEffect(() => {
  let hasScrolledEnough = false;
  let hasTimeElapsed = false;
  let eventFired = false;

  function tryFireEvent() {
    if (!eventFired && hasScrolledEnough && hasTimeElapsed) {
      eventFired = true;
      try {
        trackMetaPixel("EngagedUser", mergeCampaignParams({ scroll_pct: 40 }));
      } catch (err) {
        console.warn("Failed to fire EngagedUser event", err);
      }
    }
  }

  // TIME REQUIREMENT (17 seconds)
  const timeTimer = setTimeout(() => {
    hasTimeElapsed = true;
    tryFireEvent();
  }, 17_000);

  // SCROLL REQUIREMENT (must scroll 40% of document)
  function onScroll() {
    const doc = document.documentElement;
    const scrollTop = window.scrollY || doc.scrollTop;
    const scrollHeight = doc.scrollHeight - doc.clientHeight;
    const scrollPercent = (scrollTop / scrollHeight) * 100;

    if (scrollPercent >= 40) {
      hasScrolledEnough = true;
      tryFireEvent();
      window.removeEventListener("scroll", onScroll);
    }
  }

  window.addEventListener("scroll", onScroll);

  return () => {
    clearTimeout(timeTimer);
    window.removeEventListener("scroll", onScroll);
  };
}, [mergeCampaignParams]);


  const recordGuideDiagnostic = useCallback((eventName, payload = {}) => {
    const entry = {
      event: eventName,
      payload,
      timestamp: new Date().toISOString(),
    };

    if (typeof window !== "undefined") {
      const storeKey = "__TYK_PUBLIC_LANDING_EVENTS__";
      if (!Array.isArray(window[storeKey])) {
        window[storeKey] = [];
      }
      window[storeKey].push(entry);

      if (typeof window.dispatchEvent === "function") {
        try {
          window.dispatchEvent(new CustomEvent("tyk:publicLanding", { detail: entry }));
        } catch (error) {
          if (typeof window.Event === "function") {
            const fallbackEvent = new Event("tyk:publicLanding");
            fallbackEvent.detail = entry;
            window.dispatchEvent(fallbackEvent);
          }
        }
      }
    }

    if (typeof console !== "undefined" && typeof console.info === "function") {
      // eslint-disable-next-line no-console
      console.info(`[PublicLanding] ${eventName}`, payload);
    }
  }, []);

  const handleGuideMessageSent = useCallback(
    ({ messageCount }) => {
      recordGuideDiagnostic("GuideChatMessage", { messageCount });
      try {
        trackMetaPixel(
          "GuideChatMessage",
          mergeCampaignParams({ message_count: messageCount })
        );
      } catch (error) {
        console.warn("Meta Pixel tracking failed for GuideChatMessage", error);
      }
    },
    [mergeCampaignParams, recordGuideDiagnostic]
  );

  const handleGuideInviteAccepted = useCallback(() => {
    recordGuideDiagnostic("GuideInviteAccepted");
    try {
      trackMetaPixel("GuideInviteAccepted", mergeCampaignParams());
    } catch (error) {
      console.warn("Meta Pixel tracking failed for GuideInviteAccepted", error);
    }
  }, [mergeCampaignParams, recordGuideDiagnostic]);

  const closeGuide = () => setGuideOpen(false);
  const openLearn = useCallback(() => {
    trackCtaClick("learn_more_cta");
    setLearnOpen(true);
  }, [trackCtaClick]);
  const closeLearn = () => setLearnOpen(false);
  const openExplore = useCallback(() => {
    trackCtaClick("explore_cta");
    setExploreOpen(true);
  }, [trackCtaClick]);
  const closeExplore = () => setExploreOpen(false);

  const handleJoinNow = useCallback(
    (ctaLabel) => {
      trackCtaClick(ctaLabel);
      if (isAuthenticated) {
        history.push("/dashboard");
        return;
      }
      setJoinModalOpen(true);
    },
    [history, isAuthenticated, trackCtaClick]
  );

  const handleConfirmJoin = useCallback(async () => {
    try {
      trackMetaPixel("JoinModalContinue", mergeCampaignParams());
    } catch (error) {
      console.warn("Meta Pixel tracking failed for JoinModalContinue", error);
    }
    setJoinModalOpen(false);

    if (isAuthenticated) {
      history.push("/profile-setup");
      return;
    }

    const redirectUri =
      typeof window !== "undefined"
        ? `${window.location.origin}/profile-setup`
        : "/profile-setup";

    try {
      await loginWithRedirect({
        authorizationParams: {
          screen_hint: "signup",
          redirect_uri: redirectUri,
        },
      });
    } catch (error) {
      console.error("Auth0 redirect failed", error);
    }
  }, [history, isAuthenticated, loginWithRedirect, mergeCampaignParams]);

  const handleLogin = useCallback(async () => {
    trackCtaClick("login_cta");

    try {
      await loginWithRedirect();
    } catch (error) {
      console.error("Auth0 login redirect failed", error);
    }
  }, [loginWithRedirect, trackCtaClick]);

  const closeJoinModal = useCallback(() => {
    try {
      trackMetaPixel("JoinModalCancel", mergeCampaignParams());
    } catch (error) {
      console.warn("Meta Pixel tracking failed for JoinModalCancel", error);
    }
    setJoinModalOpen(false);
  }, [mergeCampaignParams]);

  return (
    <div className="public-landing-overhaul relative min-h-screen text-primary isolate overflow-x-hidden">
      {/* Global background */}
      <div className="absolute inset-0 z-0 pointer-events-none" aria-hidden>
        <div
          className="pl-page-backdrop-image w-full h-full bg-center [filter:brightness(1.18)_saturate(1.45)_contrast(1.08)]"
          style={{ backgroundImage: `url(${FishersOfMen})` }}
        />
        {/* lighter, no blend mode so colors stay vivid */}
        <div className="absolute inset-0 bg-black/35" />
      </div>
      <MobileCrownHeader />
      <div className="relative z-10 flex flex-col gap-10 sm:gap-12 pb-24 sm:pb-32">
        <Hero
          onJoinNow={() => handleJoinNow("UserClickedCta")}
          onLogin={handleLogin}
          showLoginButton={isProd && !isAuthenticated && !authLoading}
        />
        <StepsStrip
          onJoinNow={() => handleJoinNow("UserClickedCta")}
          onOpenLearn={openLearn}
          onOpenExplore={openExplore}
          isAuthenticated={isAuthenticated}
          authLoading={authLoading}
        />
        <KingdomMovementSection />
        <PrototypeStructure />
        <VisualExplorer />
        <JoinBand
          onJoinNow={() => handleJoinNow("UserClickedCta")}
          onLogin={handleLogin}
          showLoginButton={isProd && !isAuthenticated && !authLoading}
          isAuthenticated={isAuthenticated}
          authLoading={authLoading}
        />
      </div>

      {learnOpen && <LearnModal onClose={closeLearn} />}
      {exploreOpen && (
        <ExploreModal
          onClose={closeExplore}
          onJoinNow={() => handleJoinNow("UserClickedCta")}
          isAuthenticated={isAuthenticated}
        />
      )}
      {joinModalOpen && (
        <JoinPromptModal onClose={closeJoinModal} onContinue={handleConfirmJoin} />
      )}
      {guideOpen && (
        <>
          <div
            className="fixed inset-0 z-40 bg-black/60 backdrop-blur-sm"
            aria-hidden
            onClick={closeGuide}
          />
          <Chat
            isDarkMode
            guideOnly
            initiallyOpen
            onClose={closeGuide}
            onGuideMessageSent={handleGuideMessageSent}
            onGuideInviteAccepted={handleGuideInviteAccepted}
          />
        </>
      )}
    </div>
  );
}
function JoinPromptModal({ onClose, onContinue }) {
  const { t } = useTranslation();
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
      <div className="relative w-full max-w-xl bg-yellow-100 text-olive rounded-2xl shadow-2xl border border-yellow-300 overflow-hidden">

        {/* Top Bar */}
        <div className="bg-olive text-yellow-50 px-6 py-4 flex items-center gap-2">
          <span className="text-2xl">🔒</span>
          <h2 className="text-xl sm:text-2xl font-bold tracking-wide">
            {t('joinPrompt.title')}
          </h2>
        </div>

        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute right-4 top-4 p-2 rounded-full bg-yellow-400/20 hover:bg-yellow-400/40 transition"
          aria-label={t('common.close')}
        >
          <X className="w-5 h-5 text-yellow-50" />
        </button>

        {/* Content */}
        <div className="p-6 sm:p-7 space-y-4">
          <p className="text-lg font-semibold leading-relaxed">
            {t('joinPrompt.body1')}
          </p>
          <p className="text-base sm:text-lg leading-relaxed">
            {t('joinPrompt.body2')}
          </p>
          <p className="text-base sm:text-lg leading-relaxed">
            {t('joinPrompt.body3')}
          </p>
          {/* Buttons */}
          <div className="flex flex-col sm:flex-row sm:justify-end gap-3 pt-2">
            <button
              onClick={onClose}
              className="w-full sm:w-auto px-4 py-2 rounded-xl font-semibold border border-olive/50 text-olive hover:bg-olive/10 transition"
            >
              {t('common.cancel')}
            </button>

            <button
              onClick={onContinue}
              className="w-full sm:w-auto px-5 py-3 rounded-xl font-bold bg-green-600 text-white hover:bg-green-700 transition shadow"
            >
              {t('common.continue')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}


/* ---------------------------------- */
/* Hero                               */
/* ---------------------------------- */
function Hero({ onJoinNow, onLogin, showLoginButton }) {
  const { t } = useTranslation();
  const [activeMissionSlide, setActiveMissionSlide] = useState(0);
  const missionCarouselSlides = [
    {
      title: "Unite the Body of Christ",
      caption: "Unite the Body of Christ to advance the Kingdom of Heaven across the world. It starts with churches communing with one another, sharing strength across congregations and denominations, and building a Kingdom movement that grows from neighborhood fellowship to global communion.",
    },
    {
      title: "Allow Christ to Work Through Us",
      caption: "Just say yes to God. If you are called to do something, act on it. Post your idea so that other people in the Body of Christ can help bring it to life.",
    },
    {
      title: "Advance the Kingdom",
      caption: "A global, Christ-centered, Kingdom-minded movement designed to unite believers across the world so the Body of Christ can work together to do His will and make the world better reflect the Kingdom of Heaven.",
    },
    {
      title: "Bring Them In",
      caption: "Spread the Gospel across the Earth and help every person encounter Christ, discover their true purpose and identity, and find a place to belong.",
    },
  ];
  const activeSlide = missionCarouselSlides[activeMissionSlide];
  const showPreviousMissionSlide = () => {
    setActiveMissionSlide((current) => (current + missionCarouselSlides.length - 1) % missionCarouselSlides.length);
  };
  const showNextMissionSlide = () => {
    setActiveMissionSlide((current) => (current + 1) % missionCarouselSlides.length);
  };

  return (
    <section className="pl-hero relative z-10 px-3 sm:px-6 pt-6 pb-10 sm:pt-28 sm:pb-16">
      <Container className="pl-hero-container max-w-3xl sm:max-w-4xl lg:max-w-6xl">
        <section className="home-mission-reel pl-mission-reel" aria-label="Mission carousel">
          <video
            src={wheatfieldVideo}
            aria-hidden="true"
            className="home-mission-reel-image"
            autoPlay
            muted
            loop
            playsInline
          />
          <div className="home-mission-reel-scrim" aria-hidden="true" />
          <img src={crownLogo} alt="ThousandYearKingdom.org" className="home-mission-reel-logo" />
          <div className="home-mission-reel-content">
            <span className="home-kicker home-mission-reel-kicker">
              <span><Wheat size={16} /> Kingdom movement</span>
              <span className="home-mission-reel-count">{activeMissionSlide + 1} of {missionCarouselSlides.length}</span>
            </span>
            <h2>{activeSlide.title}</h2>
            <p>{activeSlide.caption}</p>
          </div>
          <button
            type="button"
            className="home-mission-reel-control home-mission-reel-control-prev"
            onClick={showPreviousMissionSlide}
            aria-label="Previous mission slide"
          >
            <ChevronLeft size={28} />
          </button>
          <button
            type="button"
            className="home-mission-reel-control home-mission-reel-control-next"
            onClick={showNextMissionSlide}
            aria-label="Next mission slide"
          >
            <ChevronRight size={28} />
          </button>
          <div className="home-mission-reel-dots pl-mission-reel-dots" role="tablist" aria-label="Mission slides">
            {missionCarouselSlides.map((slide, index) => (
              <button
                key={slide.title}
                type="button"
                className={index === activeMissionSlide ? "is-active" : ""}
                onClick={() => setActiveMissionSlide(index)}
                aria-label={`Show ${slide.title}`}
                aria-selected={index === activeMissionSlide}
              >
                <span>{index + 1}</span>
              </button>
            ))}
          </div>
        </section>
        <h1 className="sr-only">ThousandYearKingdom.org</h1>
      </Container>
      <div className="pl-beta-banner bg-yellow-400 text-black text-center text-sm font-semibold py-2 px-4">
        {t('landing.releaseBanner')}
      </div>
    </section>
  );
}

function MobileCrownHeader() {
  const headerRef = useRef(null);
  const [headerHeight, setHeaderHeight] = useState(0);
  const [featureBarHeight, setFeatureBarHeight] = useState(0);
  const [featureBarVisible, setFeatureBarVisible] = useState(false);

  useEffect(() => {
    if (!headerRef.current) {
      return;
    }

    const updateHeaderHeight = () => {
      if (headerRef.current) {
        setHeaderHeight(headerRef.current.offsetHeight);
      }
    };

    updateHeaderHeight();

    if (typeof ResizeObserver !== "undefined") {
      const headerResizeObserver = new ResizeObserver(updateHeaderHeight);
      headerResizeObserver.observe(headerRef.current);
      return () => headerResizeObserver.disconnect();
    }

    window.addEventListener("resize", updateHeaderHeight);
    return () => window.removeEventListener("resize", updateHeaderHeight);
  }, []);

  useEffect(() => {
    const featureBar = document.getElementById("feature-bar");
    if (!featureBar) {
      return;
    }

    const updateFeatureBarHeight = () => {
      setFeatureBarHeight(featureBar.offsetHeight || 0);
    };

    updateFeatureBarHeight();

    const intersectionObserver = new IntersectionObserver(
      ([entry]) => {
        setFeatureBarVisible(entry.isIntersecting && entry.intersectionRatio > 0);
      },
      { threshold: [0, 0.01] }
    );

    intersectionObserver.observe(featureBar);

    let featureBarResizeObserver;
    if (typeof ResizeObserver !== "undefined") {
      featureBarResizeObserver = new ResizeObserver(updateFeatureBarHeight);
      featureBarResizeObserver.observe(featureBar);
    } else {
      window.addEventListener("resize", updateFeatureBarHeight);
    }

    return () => {
      intersectionObserver.disconnect();
      if (featureBarResizeObserver) {
        featureBarResizeObserver.disconnect();
      } else {
        window.removeEventListener("resize", updateFeatureBarHeight);
      }
    };
  }, []);

  return (
    <div className="sm:hidden">
      <div style={{ height: headerHeight }} aria-hidden />
      <div
        ref={headerRef}
        className="fixed inset-x-0 z-30 w-full bg-olive/40 backdrop-blur-md border-b border-white/10"
        style={{ top: featureBarVisible ? featureBarHeight : 0 }}
      >
        <img
          src={crownLogo}
          alt="Crown Logo"
          className="w-full h-24 object-contain"
        />
      </div>
    </div>
  );
}

/* ---------------------------------- */
/* Steps strip                        */
/* ---------------------------------- */
export function StepsStrip({ onJoinNow, onOpenLearn, onOpenExplore, isAuthenticated, authLoading }) {
  const { t } = useTranslation();
  const steps = [
    {
      icon: HelpCircle,
      title: t("stepsStrip.learn.title"),
      desc: t("stepsStrip.learn.desc"),
      action: onOpenLearn,
      actionLabel: t("stepsStrip.learn.button"),
      variant: "secondary",
    },
    {
      icon: Globe2,
      title: t("stepsStrip.explore.title"),
      desc: t("stepsStrip.explore.desc"),
      action: onOpenExplore,
      actionLabel: t("stepsStrip.explore.button"),
      variant: "secondary",
    },
    {
      icon: Users,
      title: isAuthenticated ? "Continue" : t("stepsStrip.join.title"),
      desc: authLoading
        ? "Checking your account…"
        : isAuthenticated
          ? "Open your dashboard and continue serving with your groups."
          : t("stepsStrip.join.desc"),
      action: onJoinNow,
      actionLabel: authLoading
        ? null
        : isAuthenticated
          ? t("profileMenu.dashboard", "Dashboard")
          : t("stepsStrip.join.button", "Create profile"),
      variant: "primary",
    },
  ];

  const buttonClasses = (variant) =>
    variant === "primary"
      ? "pl-step-action pl-step-action-primary"
      : "pl-step-action";

  return (
    <section className="pl-steps bg-background/70 backdrop-blur border-y border-white/10">
      <Container className="pl-steps-grid">
        {steps.map(({ icon: Icon, title, desc, action, actionLabel, variant }, index) => (
          <article
            key={title}
            className="pl-step-card"
          >
            <div className="pl-step-topline">
              <div className="pl-step-icon">
                <Icon className="w-5 h-5" aria-hidden="true" />
              </div>
              <span className="pl-step-index">0{index + 1}</span>
            </div>
            <div className="pl-step-copy">
              <h3 className="pl-step-title">{title}</h3>
              <p className="pl-step-desc">{desc}</p>
            </div>
            {action && actionLabel ? (
              <button
                onClick={action}
                className={buttonClasses(variant)}
              >
                <span>{actionLabel}</span>
                <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </button>
            ) : null}
          </article>
        ))}
      </Container>
    </section>
  );
}

/* ---------------------------------- */
/* Kingdom Movement                   */
/* ---------------------------------- */
export function KingdomMovementSection() {
  const movementSteps = [
    {
      title: "Recognize the whole Body",
      body: "Congregations and denominations begin by acknowledging that the Church is larger than their own walls and that Christ did not design His Body to remain fragmented.",
      icon: Wheat,
    },
    {
      title: "Begin church to church",
      body: "Pastors communicate, people gather, churches share meals and worship, and fellowship becomes a real relationship instead of a single event.",
      icon: Users,
    },
    {
      title: "Work through what divides us",
      body: "Unity does not require us to call every difference unimportant. It requires us to face serious issues honestly and stop using secondary differences as an excuse to give up on one another.",
      icon: VoteIcon,
    },
    {
      title: "Build toward global unity",
      body: "Local fellowships connect into regional, national, and international networks until the Body can communicate, share strength, and act together across the world.",
      icon: Globe2,
    },
  ];

  return (
    <Section id="kingdom-movement" className="pl-kingdom-movement-section">
      <div className="pl-kingdom-movement-shell">
        <div className="pl-kingdom-movement-intro">
          <span className="home-kicker"><Wheat size={16} /> One Mission</span>
          <h2>The Kingdom Movement</h2>
          <p className="pl-kingdom-movement-lead">
            The Body of Christ is larger than any nation or people group on earth. Yet we often function as isolated congregations and denominations that cannot communicate, share strength, resolve disputes, or act together. Our numbers mean very little if we cannot become one Body in practice.
          </p>
        </div>

        <div className="pl-kingdom-movement-thesis">
          <article>
            <span>The problem</span>
            <h3>A Kingdom divided against itself cannot stand</h3>
            <p>
              We are called to spread the Gospel, care for people, confront darkness, and show the world what the Kingdom of Heaven is like. We cannot do that at the scale the mission demands while churches remain disconnected and repeatedly separate instead of learning how to work through conflict.
            </p>
          </article>
          <article>
            <span>The movement</span>
            <h3>Unity without forced uniformity</h3>
            <p>
              The Kingdom Movement begins when congregations and denominations recognize the Body as a whole and start putting real effort toward unity. Each church keeps its identity, leadership, finances, traditions, and internal structure. We do not ignore important issues. We face them, allow one another room for differences, and refuse to make separation our first answer.
            </p>
          </article>
          <article>
            <span>The vision</span>
            <h3>One worldwide Body capable of action</h3>
            <p>
              Imagine churches connected across entire regions, nations, and continents—able to see needs, move resources, support ministries, spread the Gospel, and carry out shared work. The Kingdom already has extraordinary numbers. The vision is to turn those numbers into relationships, trust, coordination, and a visible reflection of Heaven on earth.
            </p>
          </article>
        </div>

        <div className="pl-kingdom-movement-steps-heading">
          <span>How it begins and grows</span>
          <p>The worldwide vision begins with nearby churches choosing to take the first real step.</p>
        </div>

        <div className="pl-kingdom-movement-steps">
          {movementSteps.map(({ title, body, icon: Icon }, index) => (
            <article key={title} className="pl-kingdom-movement-step">
              <div className="pl-kingdom-movement-step-icon"><Icon size={22} aria-hidden="true" /></div>
              <span>{String(index + 1).padStart(2, "0")}</span>
              <h3>{title}</h3>
              <p>{body}</p>
            </article>
          ))}
        </div>

        <div className="pl-kingdom-movement-tool">
          <div>
            <strong>The movement needs structure, but machinery cannot move itself.</strong>
            <p>
              ThousandYearKingdom.org was purpose-built to help individuals, churches, fellowships, and massive networks communicate, make decisions, organize resources, and systematically plan and execute action together. It can preserve relationships after the first gathering and scale coordination from a few believers to a worldwide Body. But TYK is only machinery. An engine is nothing without fuel and fire. The Holy Spirit must awaken the Church, propel the movement, and direct where it goes.
            </p>
          </div>
          <Link to="/learn" className="pl-kingdom-movement-link">
            Learn how it works <ArrowRight size={17} aria-hidden="true" />
          </Link>
        </div>
      </div>
    </Section>
  );
}

/* ---------------------------------- */
/* Structure prototype                */
/* ---------------------------------- */
export function PrototypeStructure() {
  const signalCards = [
    {
      title: "Fellowship Between Churches",
      body: "Churches can enter real fellowship without surrendering their identity, leadership, finances, traditions, or internal structure. Two churches can begin, invite others, share a common space, and form fellowship circles that grow into regional and international relationships.",
      icon: Handshake,
    },
    {
      title: "From Idea to Action",
      body: "Anyone in the group can bring forward an idea. The group can discuss it, vote on it, refine the plan, propose tasks and changes, approve the finished version, and turn it into coordinated action. Ideas do not have to disappear into conversation. They can become something the Body actually carries out.",
      icon: Workflow,
    },
    {
      title: "Problem Solving Over Politics",
      body: "Differences and conflicts should not remain unresolved until people become frustrated and separate. TYK provides structured ways to identify the issue, hear the people involved, propose solutions, and reach a clear and equitable resolution. The objective is to solve the problem and preserve the Body—not build factions or reward whoever can exert the most influence.",
      icon: Scale,
    },
    {
      title: "Needs Met Through the Body",
      body: "Needs become visible so resources, funding, items, skills, and manpower can be moved toward them. Individuals, churches, ministries, businesses, and groups can respond with what they have. The strength of the wider Body can be activated where one person or congregation cannot carry the burden alone.",
      icon: HandCoins,
    },
    {
      title: "A Kingdom Economy",
      body: "TYK provides a marketplace for goods, barter, services, employment, and Christian businesses. The goal is an economy shaped by honest dealing, generosity, fair exchange, useful work, trustworthy reputation, and care for people—not simply extracting as much money as possible from every transaction.",
      icon: Store,
    },
    {
      title: "Coordination Across Massive Numbers",
      body: "TYK coordinates individuals, groups, churches, fellowships, regions, and nations without creating another political hierarchy over the Church. Anyone can bring forward an idea. Groups make their own decisions, coordinate with other groups, and join larger actions without waiting for politicians or a central institution to decide what the Body is allowed to do.",
      icon: Network,
    },
  ];

  return (
    <Section className="home-structure-section">
      <div className="home-section-header">
        <span className="home-kicker"><HandCoins size={16} /> From conviction to coordinated action</span>
        <h2>The Kingdom Operational</h2>
        <p>
          TYK gives the Body more than a place to communicate. It provides structured ways to form relationships, make decisions, solve problems, move resources, build a Kingdom economy, and coordinate action across enormous numbers of people without placing another governing institution over the Church.
        </p>
      </div>

      <div className="home-feature-grid home-operational-grid">
        {signalCards.map(({ title, body, icon: Icon }) => (
          <article key={title} className="home-feature-card">
            <div className="home-card-icon"><Icon size={28} /></div>
            <h3>{title}</h3>
            <p>{body}</p>
          </article>
        ))}
      </div>
    </Section>
  );
}

/* ---------------------------------- */
/* Visual Explorer (Globe + copy)     */
/* ---------------------------------- */
function VisualExplorer() {
  const { t } = useTranslation();
  return (
    <Section className="pl-globe-section pt-4">
      <div className="pl-section-heading">
        <span><Globe2 className="w-4 h-4" /> {t('hero.fieldKicker')}</span>
        <h2>{t('globe.heading')}</h2>
        <p>
          {t('globe.body')}
        </p>
      </div>
      <div className="pl-scripture-callout max-w-3xl sm:max-w-4xl lg:max-w-6xl mx-auto">
        <p>
          {t('hero.genesisLight')}
        </p>
      </div>
      <div className="pl-globe-theatre rounded-3xl overflow-visible ring-1 ring-white/10 bg-black/20 max-w-3xl sm:max-w-4xl lg:max-w-6xl mx-auto">
        <Suspense
          fallback={
            <div className="h-[360px] sm:h-[520px] grid place-items-center text-white/80">
              {t('globe.loading')}
            </div>
          }
        >
          <GlobeWithPins
            darkMode={true}
            heightClass="h-[520px] sm:h-[640px] md:h-[760px]"
          />
        </Suspense>
      </div>
    </Section>
  );
}

/* ---------------------------------- */
/* Join CTA band                      */
/* ---------------------------------- */
function JoinBand({ onJoinNow, onLogin, showLoginButton, isAuthenticated, authLoading }) {
  const { t } = useTranslation();
  return (
    <Section>
      <div className="pl-join-band bg-olive/40 rounded-3xl p-6 md:p-8 flex flex-col md:flex-row items-center md:items-start justify-between gap-5 ring-1 ring-white/10 backdrop-blur text-center md:text-left max-w-3xl lg:max-w-5xl mx-auto">
        <div className="space-y-2">
          <h3 className="text-2xl font-bold text-white">
            {isAuthenticated ? "Welcome back" : t("landing.joinHeading", "Ready to start?")}
          </h3>
          <p className="text-white/90 text-base">
            {authLoading
              ? "Checking your account…"
              : isAuthenticated
                ? "Continue to your dashboard to coordinate, serve, and build with your groups."
                : t(
                    "landing.joinCopy",
                    "Sign in or sign up to finish your profile and join the movement."
                  )}
          </p>
        </div>
        <div className="w-full md:w-auto flex flex-col sm:flex-row gap-3">
          <button
            onClick={onJoinNow}
            disabled={authLoading}
            className="w-full sm:w-auto bg-yellow-500 hover:bg-yellow-600 text-olive font-semibold px-6 py-3 rounded-xl shadow"
          >
            {authLoading
              ? "Checking account…"
              : isAuthenticated
                ? t("profileMenu.dashboard", "Dashboard")
                : t("landing.ctaPrimary", "Create Profile")}
          </button>
          {showLoginButton ? (
            <button
              onClick={onLogin}
              className="w-full sm:w-auto bg-white/10 hover:bg-white/20 text-white font-semibold px-6 py-3 rounded-xl border border-white/25"
            >
              {t("profileMenu.logIn", "Log In")}
            </button>
          ) : null}
        </div>
      </div>
    </Section>
  );
}
